import React, { ReactElement, useState } from "react";
import { cva, VariantProps } from "class-variance-authority";
import clsx from "clsx";

export const avatarVariants = cva(
  "inline-flex items-center justify-center shrink-0 rounded-full font-medium cursor-default select-none border-secondary",
  {
    variants: {
      size: {
        xs: "w-5 h-5 text-4xs border-[0.25px]",
        sm: "w-6 h-6 text-3xs border-[0.25px]",
        md: "w-8 h-8 text-sm border-[0.50px]",
        lg: "w-10 h-10 text-base border-[0.50px]",
        xl: "w-12 h-12 text-xl border-[0.75px]",
        "2xl": "w-20 h-20 text-3xl border-[0.75px]",
        "3xl": "w-28 h-28 text-5xl border",
        "4xl": "w-40 h-40 text-6xl border",
      },
      color: {
        primary: "bg-brand-primary text-brand-primary",
        secondary: "bg-brand-secondary text-brand-secondary",
        success: "bg-success-primary text-success",
        warning: "bg-warning-primary text-warning",
        error: "bg-error-primary text-error",
      },
    },
    defaultVariants: {
      size: "md",
      color: "primary",
    },
  },
);

export type AvatarProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof avatarVariants> & {
    imageUrl?: string | null;
    initials?: string;
    alt?: string;
    showTitle?: boolean;
    fallbackElement?: ReactElement;
  };

const Avatar = ({
  size,
  color,
  imageUrl,
  initials,
  alt = "User Avatar",
  showTitle = true,
  className,
  fallbackElement,
  ...props
}: AvatarProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isError, setIsError] = useState(false);

  if (isError && fallbackElement && imageUrl) {
    return fallbackElement;
  }

  return (
    <div className={clsx(avatarVariants({ size, color }), className)} title={showTitle ? alt : undefined} {...props}>
      {imageUrl ? (
        <>
          <img
            src={imageUrl}
            alt={alt}
            onError={() => setIsError(true)}
            onLoad={() => setIsLoaded(true)}
            className={clsx("h-full w-full rounded-full object-cover", { visible: isLoaded, invisible: !isLoaded })}
          />
          {!isLoaded && <div className="h-full w-full animate-pulse rounded-full" />}
        </>
      ) : (
        fallbackElement || initials || "?"
      )}
    </div>
  );
};

export default Avatar;
